import { useEffect, useRef, useCallback, useState } from 'react'
import { io } from 'socket.io-client'

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ||
  (import.meta.env.PROD ? window.location.origin : 'http://localhost:5001')

export function useEventQuizSocket({ roomCode, role, participantId, hostSecret, enabled = true }) {
  const socketRef = useRef(null)
  const [connected, setConnected] = useState(false)
  const [state, setState] = useState(null)
  const listenersRef = useRef({})

  const on = useCallback((event, handler) => {
    listenersRef.current[event] = handler
    if (socketRef.current) {
      socketRef.current.on(event, handler)
    }
  }, [])

  const emit = useCallback((event, data) => {
    socketRef.current?.emit(event, data)
  }, [])

  useEffect(() => {
    if (!enabled || !roomCode) return

    const socket = io(SOCKET_URL, { transports: ['websocket', 'polling'] })
    socketRef.current = socket

    socket.on('connect', () => {
      setConnected(true)
      socket.emit('event-join', { roomCode, role, participantId, hostSecret })
    })

    socket.on('disconnect', () => setConnected(false))

    socket.on('event-state', (data) => setState(data))

    Object.entries(listenersRef.current).forEach(([event, handler]) => {
      socket.on(event, handler)
    })

    return () => {
      socket.disconnect()
      socketRef.current = null
      setConnected(false)
    }
  }, [roomCode, role, participantId, hostSecret, enabled])

  useEffect(() => {
    const socket = socketRef.current
    if (!socket) return

    const register = (event, handler) => {
      socket.on(event, handler)
      return () => socket.off(event, handler)
    }

    return register
  }, [connected])

  return { connected, state, on, emit, socket: socketRef }
}

export function playSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.connect(gain)
    gain.connect(ctx.destination)

    if (type === 'first-place') {
      osc.frequency.setValueAtTime(523, ctx.currentTime)
      osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1)
      osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2)
      gain.gain.setValueAtTime(0.3, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.5)
    } else if (type === 'correct') {
      osc.frequency.setValueAtTime(880, ctx.currentTime)
      gain.gain.setValueAtTime(0.2, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.2)
    } else if (type === 'tick') {
      osc.frequency.setValueAtTime(440, ctx.currentTime)
      gain.gain.setValueAtTime(0.1, ctx.currentTime)
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.1)
    } else if (type === 'jackpot') {
      [523, 659, 784, 1047].forEach((freq, i) => {
        const o = ctx.createOscillator()
        const g = ctx.createGain()
        o.connect(g)
        g.connect(ctx.destination)
        o.frequency.setValueAtTime(freq, ctx.currentTime + i * 0.15)
        g.gain.setValueAtTime(0.25, ctx.currentTime + i * 0.15)
        g.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.15 + 0.3)
        o.start(ctx.currentTime + i * 0.15)
        o.stop(ctx.currentTime + i * 0.15 + 0.3)
      })
    }
  } catch {
    // Audio non disponible
  }
}
